import { DATE_FORMAT, DEFAULT_LOCALE } from 'constants/general';
import dayjs from 'dayjs';
import { t } from 'i18next';

import {
  ARRAY_PG_TYPE_SUFFIX, BOOLEAN_PG_TYPE, DATE_PG_TYPES, JSON_PG_TYPES, NUMERIC_PG_TYPES,
} from '../constants/datasource';

/** A number type is right-aligned in the table; every other type reads from the left. */
export const isNumericPgType = (pgType: string): boolean => NUMERIC_PG_TYPES.includes(pgType);

/**
 * One cell of a datasource, as text, by the Postgres type of its column.
 *
 * A null is an empty cell. A date is a date, a number a number, an array its items joined, JSON
 * compact, a boolean yes or no. Anything else — text, an enum, a type this UI does not know — is
 * the value as text, so no cell is ever hidden.
 */
const formatDatasourceCell = (value: unknown, pgType: string): string => {
  if (value === null || value === undefined) {
    return '';
  }

  if (pgType.endsWith(ARRAY_PG_TYPE_SUFFIX)) {
    return Array.isArray(value) ? value.map((item) => String(item)).join(', ') : String(value);
  }

  if (JSON_PG_TYPES.includes(pgType)) {
    return JSON.stringify(value);
  }

  if (pgType === BOOLEAN_PG_TYPE) {
    return t(value ? 'aiChat.tools.datasourceYes' : 'aiChat.tools.datasourceNo');
  }

  if (DATE_PG_TYPES.includes(pgType)) {
    const date = dayjs(String(value));

    return date.isValid() ? date.format(DATE_FORMAT) : String(value);
  }

  if (isNumericPgType(pgType) && typeof value === 'number') {
    return value.toLocaleString(DEFAULT_LOCALE, { maximumFractionDigits: 6 });
  }

  return typeof value === 'object' ? JSON.stringify(value) : String(value);
};

export default formatDatasourceCell;
